import { StockLabel } from '~/lib/salesforce/utilities/product-stock';
import { isFreightByCarrier } from '~/lib/salesforce/utilities/shipping';
import { getSoldOutCTAOverride } from '~/shared/utils/get-sold-out-cta-override';
import { isDiscontinuedStoreFormat } from '~/shared/utils';
import { toBoolean } from '~/shared/utils/toBoolean';
import { ICTAButton, TProductDetails } from '../../ProductCard/ProductCard.definition';

type ProductStockInput = Pick<
    TProductDetails,
    | 'ctaButtons'
    | 'cust_in_stock_store'
    | 'cust_in_stock_web'
    | 'cust_storeFormat'
    | 'cust_displayUntilDate'
    | 'c_itemFreightGroup'
    | 'cust_freight_group'
    | 'notifications'
>;

const isValidProductButton = (button: unknown): button is ICTAButton => {
    if (!button || typeof button !== 'object') return false;

    return typeof (button as Partial<ICTAButton>).translationKey === 'string';
};

const hasAvailableButton = (button: unknown, translationKey: string): boolean => {
    if (!isValidProductButton(button)) return false;

    return (
        button.translationKey === translationKey &&
        !toBoolean(button.isDisabled ?? false) &&
        toBoolean(button.shouldRender ?? true)
    );
};

const getOnlineStockLabel = ({
    hasOnlineStock,
    hasFewOnlineStock,
    canNotifyWhenBack,
    isPermanentlyOutOfStockOnline,
    isDiscontinued,
}: {
    hasOnlineStock: boolean;
    hasFewOnlineStock: boolean;
    canNotifyWhenBack: boolean;
    isPermanentlyOutOfStockOnline: boolean;
    isDiscontinued: boolean;
}): StockLabel | null => {
    if (hasOnlineStock) {
        return {
            key: hasFewOnlineStock ? 'plp.lowStockOnlineLabel' : 'plp.inStockOnlineLabel',
            tone: 'green',
        };
    }

    if (canNotifyWhenBack) return { key: 'plp.notifyOnlineLabel', tone: 'grey' };

    if (isPermanentlyOutOfStockOnline) {
        return {
            key: isDiscontinued ? 'plp.discontinuedOnlineLabel' : 'plp.outofstockpermanentlyonline',
            tone: 'red',
            isTextGrey: true,
        };
    }

    return null;
};

const getStoreStockLabel = ({
    hasStoreStock,
    canDeliverToStore,
}: {
    hasStoreStock: boolean;
    canDeliverToStore: boolean;
}): StockLabel | null => {
    if (hasStoreStock) return { key: 'plp.inStockStoreLabel', tone: 'green' };

    if (canDeliverToStore) return { key: 'plp.deliverToStoreLabel', tone: 'yellow' };

    return null;
};

export const getStockLabels = (product: ProductStockInput): StockLabel[] => {
    // Products enrolled in the "Display until date" feature get their CTA decided from real
    // stock (see getSoldOutCTAOverride).
    const soldOutCTA = getSoldOutCTAOverride({
        isEnrolled: Boolean(product.cust_displayUntilDate),
        hasOnlineStock: Boolean(product.cust_in_stock_web),
        hasStoreStock: Boolean(product.cust_in_stock_store),
        realPrimary: product.ctaButtons?.primary,
        realSecondary: product.ctaButtons?.secondary,
    });

    const primaryButton = soldOutCTA.primary;
    const secondaryButton = soldOutCTA.secondary;
    const primaryTranslationKey = String(primaryButton?.translationKey ?? '');

    // Online stock is derived from enabled CTA actions.
    const hasOnlineStock =
        hasAvailableButton(primaryButton, 'addtocart') ||
        hasAvailableButton(primaryButton, 'navigateto');

    // Primary notifications mark low online stock.
    const hasFewOnlineStock =
        product.notifications?.some(({ type }) => type === 'primary') ?? false;

    const hasStoreStock =
        product.cust_in_stock_store === true || hasAvailableButton(secondaryButton, 'findinstore');

    // Carrier freight cannot be delivered to store.
    const hasCarrierFreight = isFreightByCarrier(
        product.c_itemFreightGroup ?? product.cust_freight_group,
    );

    // Store delivery shown only when online stock can fulfill it.
    const canDeliverToStore = hasOnlineStock && !hasStoreStock && !hasCarrierFreight;

    // Notify label shown for temporary online out-of-stock states.
    const canNotifyWhenBack =
        !hasOnlineStock &&
        (primaryTranslationKey === 'comingsoon' ||
            primaryTranslationKey === 'comingsoononline' ||
            primaryTranslationKey === 'outofstock');

    // Out of stock online and in stores, with no known restock date. Unrelated to
    // discontinued/obsolete products, which redirect away from the PDP and never appear here.
    const isPermanentlyOutOfStock = primaryTranslationKey === 'outofstockpermanently';
    const isPermanentlyOutOfStockOnline = primaryTranslationKey === 'outofstockpermanentlyonline';
    const isDiscontinued = isDiscontinuedStoreFormat(product.cust_storeFormat);

    // Sold out everywhere (online and in stores) and will not come back.
    if (isPermanentlyOutOfStock) {
        return [{ key: 'plp.permanentlyOutOfStockLabel', tone: 'red', isTextGrey: true }];
    }

    const onlineLabel = getOnlineStockLabel({
        hasOnlineStock,
        hasFewOnlineStock,
        canNotifyWhenBack,
        isPermanentlyOutOfStockOnline,
        isDiscontinued,
    });

    const storeLabel = getStoreStockLabel({ hasStoreStock, canDeliverToStore });

    return [onlineLabel, storeLabel].filter((label): label is StockLabel => label !== null);
};
